/**
 * Thin wrappers around orval-generated API key hooks.
 *
 * Mirrors useModels.ts: unwrap the orval v8 `{data, status, headers}` shape
 * into a flat result and centralize list-cache invalidation so the settings
 * UI doesn't repeat queryKey plumbing.
 */

import { useQueryClient, type UseMutationResult } from '@tanstack/react-query'

import type { ApiRequestError } from '@/api/client'
import {
  type createApiKeyApiApiKeysPostResponse,
  getListApiKeysApiApiKeysGetQueryKey,
  useCreateApiKeyApiApiKeysPost,
  useListApiKeysApiApiKeysGet,
  useRevokeApiKeyApiApiKeysKeyIdDelete,
} from '@/api/generated/api-keys/api-keys'
import type {
  ApiKeyCreateRequest,
  ApiKeyCreateResponse,
  ApiKeyListItem,
} from '@/api/generated/model'

export type ApiKey = ApiKeyListItem
export type { ApiKeyCreateRequest, ApiKeyCreateResponse }

interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  error: ApiRequestError | null
  isError: boolean
}

export function useApiKeys(): UnwrappedQuery<ApiKey[]> {
  const query = useListApiKeysApiApiKeysGet()
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useCreateApiKey(): UseMutationResult<
  createApiKeyApiApiKeysPostResponse,
  ApiRequestError,
  { data: ApiKeyCreateRequest }
> {
  const queryClient = useQueryClient()
  return useCreateApiKeyApiApiKeysPost({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getListApiKeysApiApiKeysGetQueryKey(),
        })
      },
    },
  })
}

export function useRevokeApiKey(): UseMutationResult<
  unknown,
  ApiRequestError,
  { keyId: string }
> {
  const queryClient = useQueryClient()
  return useRevokeApiKeyApiApiKeysKeyIdDelete({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getListApiKeysApiApiKeysGetQueryKey(),
        })
      },
    },
  })
}
